import React, { useCallback, useMemo } from 'react'
import { Button, Col, Grid, Icon, List, Typography } from 'gu-ui-kit'
import EmptyResults from 'components/EmptyResults'
import { useTableConfig } from 'modules/MediaPlanner/subcomponents/Table/hooks/useTableConfig'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import MultipleSelect from 'components/MultipleSelect'
import downloadFile from 'utils/downloadFile'
import { notificationShow } from 'modules/Notifications/actions'
import GridSpinner from 'components/UI/GridSpinner'
import { URL_FILE_PDF, URL_FILE_XLS } from 'api/media-planner/v1'
import { isBoolean } from 'lodash'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/MediaPlanner/MediaPlanner.module.scss'
import qs from 'qs'

export const Table = ({ form = 'media-planner', benchmark, config }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const { mediaPlanner = {}, form: formValues = {} } = useSelector(
    (state) => ({
      mediaPlanner: state[form],
      form: state.form?.[form]?.values ?? {},
    }),
    shallowEqual,
  )

  const dispatch = useDispatch()

  const [tableSettings, setTableSettings] = useLocalStorage('mediaPlannerTableSettings', {
    columnVisibility: {},
  })

  const {
    target_results: { data: { items: data = [], summary = {}, loading = false } = {} } = {},
  } = mediaPlanner

  const { tableState, tableConfig, sorting } = useTableConfig({
    data,
    summary,
    config,
    columnVisibility: tableSettings.columnVisibility,
  })

  const handleChange = useCallback(
    (values) => {
      setTableSettings({
        columnVisibility: tableConfig.columns.reduce(
          (acc, { id }) => ({ ...acc, [id]: values.includes(id) }),
          {},
        ),
      })
    },
    [tableConfig],
  )

  const handleReset = useCallback(
    () =>
      setTableSettings({
        columnVisibility: {},
      }),
    [],
  )

  const handleDownloadError = useCallback(
    (error) =>
      dispatch(
        notificationShow({
          type: 'warning',
          message: error,
          delay: 20000,
        }),
      ),
    [dispatch],
  )

  const exportUrlParams = useMemo(() => {
    const { apiDependencies = [] } = config?.['target_results'] ?? {}
    const sortingProps = {}
    const { id = null, desc = null } = sorting?.[0] ?? {}
    if (id) {
      sortingProps.sort_by = id
      sortingProps.sort_direction = desc ? 'desc' : 'asc'
    }

    const params = apiDependencies.reduce(
      (acc, dependency) =>
        formValues?.[dependency]?.length > 0
          ? { ...acc, [dependency]: formValues?.[dependency] }
          : acc,
      {},
    )

    return tableSettings.columnVisibility && Object.keys(params).length === apiDependencies.length
      ? qs.stringify({
          ...params,
          columns: Object.entries(tableSettings.columnVisibility).reduce(
            (acc, [k, v]) => (v ? [...acc, k] : acc),
            [],
          ),
          ...sortingProps,
        })
      : null
  }, [formValues, tableSettings.columnVisibility, sorting, config])

  const visibilityColumnValue = useMemo(
    () =>
      tableConfig.columns.reduce(
        (acc, { id }) =>
          !isBoolean(tableSettings.columnVisibility?.[id]) || tableSettings.columnVisibility?.[id]
            ? [...acc, id]
            : acc,
        [],
      ),
    [tableSettings.columnVisibility, tableConfig.columns],
  )

  const visibilityColumnOptions = useMemo(
    () =>
      tableConfig.columns.map(({ id }) => {
        const postfix = config?.target_results?.tableConfig?.columns?.[id]?.postfix ?? false
        return {
          label: t(`media_planner::table.header.${id}.label`) + (postfix ? `, ${postfix}` : ''),
          value: id,
        }
      }),
    [tableConfig.columns, breakpoint, config],
  )

  if (loading) {
    return <GridSpinner width={32} />
  }

  return (
    <>
      {data.length > 0 && (
        <Grid
          className={'margin-bottom-16'}
          justifyContent={'between'}
          alignItems={'center'}
        >
          <Col>
            <MultipleSelect
              name={'visibilityColumn'}
              value={visibilityColumnValue}
              values={visibilityColumnOptions}
              placeholder={t('media_planner::buttons.cols.label')}
              apply={t(`common::buttons.apply`)}
              dropSelection={t(`common::buttons.reset`)}
              onChange={handleChange}
              onReset={handleReset}
            />
          </Col>
          <Col>
            <Grid
              gap={16}
              wrap={false}
              fluid={false}
              alignItems={'center'}
            >
              {exportUrlParams && (
                <>
                  {breakpoint.md && (
                    <Typography
                      variant={'Body-15'}
                      color={'Label-Secondary'}
                    >
                      {t('media_planner::buttons.caption')}
                    </Typography>
                  )}

                  <Button
                    component={'a'}
                    onClick={() =>
                      downloadFile(
                        `${URL_FILE_XLS({ benchmark })}?${exportUrlParams}`,
                        'media-planner',
                        'xls',
                      ).catch(handleDownloadError)
                    }
                    target={'_blank'}
                    variant={'ghost'}
                    size={'m'}
                    grey
                    noLabel
                    startIcon={
                      <Icon
                        name={'file-xls'}
                        color={'Interface-Eighth'}
                        size={'m'}
                      />
                    }
                  />

                  <Button
                    component={'a'}
                    onClick={() =>
                      downloadFile(
                        `${URL_FILE_PDF({ benchmark })}?${exportUrlParams}`,
                        'media-planner',
                        'pdf',
                      ).catch(handleDownloadError)
                    }
                    target={'_blank'}
                    variant={'ghost'}
                    size={'m'}
                    grey
                    noLabel
                    startIcon={
                      <Icon
                        name={'file-pdf'}
                        color={'Interface-Eighth'}
                        size={'m'}
                      />
                    }
                  />
                </>
              )}
            </Grid>
          </Col>
        </Grid>
      )}
      <div className={styles['media-planner__table-wrapper']}>
        <List
          table={tableState}
          config={tableConfig}
          EmptyComponent={() => (
            <EmptyResults
              header={t('media_planner::table.empty_results.heading')}
              text={t('media_planner::table.empty_results.text', {
                buttonLabel: t('common::buttons.calculate'),
              })}
              icon={<Icon name={'task'} />}
            />
          )}
        />
      </div>
    </>
  )
}

Table.propTypes = {
  form: PropTypes.string,
  benchmark: PropTypes.string,
  config: PropTypes.shape({
    target_results: PropTypes.object,
    fields: PropTypes.object,
  }),
}
